import { FilterIcon } from '@heroicons/react/solid';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDetectClickOutside } from 'react-detect-click-outside';

import DownArrow from '../../svgs/DownArrow';
import useOnClickOutside from '../../../hooks/useClickOutside';

const TidalDropdownWrapper = styled.div`
  padding: 0.25rem 0px;
  width: 100%;
  outline: none;
`;

const TidalContainerLabel = styled.p`
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  text-align: left;
  margin-bottom: 7px;
  color: ${(props) => props.theme.colors.text[0]};
  position: relative;
  width: fit-content;
  & > span {
    position: absolute;
    right: -10px;
    bottom: 4px;
    color: ${(props) => props.theme.colors.text[3]};
  }
`;

const TidalDropdownContainer = styled.button`
  border: 1px solid ${(props) => props.theme.colors.borderColors[0]};
  border-radius: 10px;
  background-color: ${(props) => props.theme.colors.secondary};
  height: 42px;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 12px;
  grid-gap: 0 10px;
  align-items: center;
  padding: 0 13px;
  cursor: pointer;
  position: relative;
  &:focus,
  &:active {
    outline: 2px solid ${(props) => props.theme.colors.accent};
  }
  & > .selected-item {
    font-weight: 600;
    font-size: 14px;
    line-height: 17px;
    color: ${(props) => props.theme.colors.text[1]};
    display: flex;
    justify-content: flex-start;
  }
`;

const TidalDropdownMenuContainer = styled.div<{ isVisible: boolean }>`
  border: ${(props) =>
    props.isVisible
      ? `1px solid ${props.theme.colors.borderColors[0]}`
      : 'none'};
  border-radius: 10px;
  box-shadow: 0px 10px 49px rgb(0 27 31 / 12%);
  position: absolute;
  top: 50px;
  width: 100%;
  background-color: ${(props) => props.theme.colors.secondary};
  max-height: 200px;
  height: fit-content;
  z-index: 1;
  padding: ${(props) => (props.isVisible ? '8px' : '0 8px')};
  transition: all ease-in-out;
  overflow-y: auto;
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
  display: ${(props) => (props.isVisible ? '' : 'hidden')};
`;

const TidalDropdownMenuItem = styled.div<{ isDropDownOpened: boolean }>`
  height: 20px;
  width: 100%;
  border-radius: 4px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: #000;
  font-size: 14px;
  display: ${(props) => (props.isDropDownOpened ? '' : 'hidden')};
  &:hover {
    background-color: ${(props) => `${props.theme.colors.accent}80`};
    color: #fff;
  }
`;

interface ITidalDropdown {
  label: string;
  items: string[];
  isRequired?: boolean;
  defaultValue?: string;
  onSelect: (item: string, i?: number) => void;
}

const TidalDropdown = ({
  label,
  items,
  isRequired,
  onSelect,
  defaultValue,
}: ITidalDropdown): JSX.Element => {
  const [selectedItem, setSelectedItem] = useState(items[0]);
  const [isDropdownOpened, setIsDropDownOpened] = useState(false);
  const dropdownMenuRef = useRef<any>();

  useEffect(() => {
    if (defaultValue) {
      setSelectedItem(defaultValue);
      return;
    }
    setSelectedItem(items[0]);
  }, [defaultValue]);

  useEffect(() => {
    if (!items.includes(selectedItem)) {
      setSelectedItem(defaultValue || null);
    }
  }, [items]);

  const ref = useRef();
  useOnClickOutside(ref, () => {
    if (isDropdownOpened) handleDropDownToggle();
  });

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const handleDropDownToggle = async () => {
    try {
      if (!isDropdownOpened) {
        setIsDropDownOpened(true);
      } else {
        setIsDropDownOpened(false);
      }
    } catch (e) {}
  };

  const handleItemSelect = (item, i) => {
    setSelectedItem(item);
    handleDropDownToggle();
    onSelect(item, i);
  };

  return (
    <TidalDropdownWrapper>
      <TidalContainerLabel>
        {label}
        {isRequired && <span>*</span>}
      </TidalContainerLabel>
      <TidalDropdownContainer
        ref={ref}
        onClick={() => {
          handleDropDownToggle();
        }}
      >
        <div className="selected-item">{selectedItem}</div>
        <div>
          <DownArrow />
        </div>
        <TidalDropdownMenuContainer
          isVisible={isDropdownOpened}
          ref={dropdownMenuRef}
        >
          {items.map((item, key) => (
            <TidalDropdownMenuItem
              key={key}
              onClick={(e) => {
                e.stopPropagation();
                handleItemSelect(item, key);
              }}
              isDropDownOpened={isDropdownOpened}
            >
              {item}
            </TidalDropdownMenuItem>
          ))}
        </TidalDropdownMenuContainer>
      </TidalDropdownContainer>
    </TidalDropdownWrapper>
  );
};

export default TidalDropdown;
